'use client';

import React, { useCallback, useState } from 'react';
import { PageHeader } from 'src/components/Settings/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowUp,
  faEnvelope,
  faUpload,
} from '@fortawesome/pro-regular-svg-icons';
import { BetaFlag, Button, ErrorState, Input } from 'src/components';
import { useNotification } from 'src/context/notification';
import { useUploadEmailListMutation } from 'src/api/redux';

interface FormState {
  emails: File | null;
}

interface FormErrors {
  emails?: string;
}

interface ErrorStateProps {
  title: string;
  description: string;
}

const EmailUploader = () => {
  const { setNotification } = useNotification();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [formState, setFormState] = useState<FormState>({
    emails: null,
  });

  const [preview, setPreview] = useState<any[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [errorState, setErrorState] = useState<ErrorStateProps | null>(null);

  // RTK Query mutation
  const [uploadEmailList, { isLoading: isEmailsUploading }] =
    useUploadEmailListMutation();

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formState.emails) {
      newErrors.emails = 'Please upload a file';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState]);

  const processFile = useCallback((selectedFile: File) => {
    // Reset states
    setErrors((prev) => ({ ...prev, emails: undefined }));

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      setErrors((prev) => ({ ...prev, emails: 'Please upload a CSV file' }));
      return;
    }

    setFormState((prev) => ({ ...prev, emails: selectedFile }));

    // Read and preview the file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n');

        // Check if header is correct (allow spaces around comma)
        const normalizedHeader = lines[0]
          ?.trim()
          .toLowerCase()
          .replace(/\s+/g, '');
        if (normalizedHeader !== 'provider_id,email') {
          setErrors((prev) => ({
            ...prev,
            emails: 'CSV must have "provider_id,email" as the header',
          }));
          return;
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, emails: 'Error reading file' }));
      }
    };
    reader.readAsText(selectedFile);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        processFile(droppedFile);
      }
    },
    [processFile]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;
      processFile(selectedFile);
    },
    [processFile]
  );

  const resetForm = useCallback(() => {
    setFormState({
      emails: null,
    });
    setPreview([]);
    setErrors({});
    setErrorState(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!validateForm()) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', formState.emails as File);

      await uploadEmailList(formData).unwrap();
      setNotification({
        title: 'Email list uploaded successfully',
        message: 'Your email list has been uploaded successfully',
        type: 'success',
      });
      resetForm();
    } catch (err) {
      setErrorState({
        title: 'Upload failure',
        description: err.error_message,
      });
    } finally {
      setIsUploading(false);
    }
  }, [formState, validateForm, resetForm]);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader>
        <div className="flex items-center gap-2">
          <Icon icon={faEnvelope} className="text-neutral-500 text-lg" />
          <h2 className="text-lg font-semibold text-primary">Email Uploader</h2>
          <BetaFlag />
        </div>
        <p className="text-xs text-primary font-semibold">
          Upload a list of email addresses to update the database.
        </p>
      </PageHeader>
      <div className="bg-white shadow-sm flex flex-col border border-neutral-100 rounded-lg p-6">
        <div className="flex flex-col gap-4">
          {/* Dropzone */}
          <div
            className={`border-2 border-dashed rounded-lg p-4 w-full min-h-64
                ${
                  isDragging
                    ? 'border-sky-500'
                    : 'border-neutral-200 hover:border-sky-300'
                }
                ${errors.emails ? 'border-red-500' : ''}
                transition-all duration-300 ease-in-out
                cursor-pointer
                flex items-center justify-center
                `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            aria-label="File upload area"
          >
            <div className="flex flex-col items-center gap-4 w-full">
              <Icon
                icon={faCloudArrowUp}
                className={`text-4xl transition-colors duration-300 ease-in-out ${
                  isDragging ? 'text-sky-500' : 'text-neutral-200'
                }`}
              />
              <p className="text-sm text-neutral-500">
                Drag and drop your CSV file here or click to upload
              </p>
              <div
                className="mt-2 p-3 bg-neutral-50 rounded-md border border-neutral-200"
                data-testid="example-format"
              >
                <p className="text-xs text-neutral-500 mb-2">
                  Example CSV Format:
                </p>
                <div className="bg-white p-2 rounded border border-neutral-200 overflow-x-auto">
                  <table
                    className="text-xs text-neutral-600 border-collapse w-full"
                    data-testid="example-table"
                  >
                    <thead>
                      <tr className="bg-neutral-200">
                        <th className="border border-neutral-300 px-2 py-1 text-left font-semibold">
                          provider_id
                        </th>
                        <th className="border border-neutral-300 px-2 py-1 text-left font-semibold">
                          email
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-neutral-300 px-2 py-1">
                          123456789
                        </td>
                        <td className="border border-neutral-300 px-2 py-1">
                          bad_email@aol.com
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-neutral-300 px-2 py-1">
                          987654321
                        </td>
                        <td className="border border-neutral-300 px-2 py-1">
                          other_bad_email@netscape.org
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <Input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-upload-input"
                id="file-upload"
              />
              <div
                className="inline-flex items-center justify-center gap-2 px-4
                  py-2 bg-neutral-50 text-neutral-700 rounded-md
                  cursor-pointer hover:bg-neutral-100 transition-colors duration-300 ease-in-out"
                data-testid="choose-file-button"
              >
                <Icon icon={faUpload} className="text-neutral-500" size="sm" />
                <span className="text-xs">Choose CSV File</span>
              </div>
              <p className="text-xs text-neutral-500">
                Supported file types: CSV
              </p>
              {errors.emails && (
                <p
                  className="text-xs text-red-500 transition-opacity duration-300 ease-in-out"
                  data-testid="file-error"
                >
                  {errors.emails}
                </p>
              )}
              <div
                className={`w-full transition-all duration-300 ease-in-out ${
                  formState.emails
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-2 h-0'
                }`}
              >
                {formState.emails && !errors.emails ? (
                  <div
                    className="flex flex-col items-center gap-2 p-4 bg-sky-50 border border-sky-200 rounded-lg w-full"
                    data-testid="file-preview"
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        icon={faCloudArrowUp}
                        className="text-sky-500 transition-colors duration-300 ease-in-out"
                      />
                      <p className="text-sm font-medium text-sky-600">
                        File Selected
                      </p>
                    </div>
                    <p className="text-sm text-sky-600">
                      {formState.emails.name}
                    </p>
                    {preview.length > 0 && (
                      <div className="text-xs text-sky-600 bg-sky-50 rounded-full font-normal px-2 py-1 transition-colors duration-300 ease-in-out">
                        {preview.length} IDs were found in the file
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex justify-end w-full gap-2">
            <Button
              variant="outlined"
              color="neutral"
              className="rounded-full w-40"
              onClick={resetForm}
              disabled={isUploading || isEmailsUploading}
              data-testid="reset-button"
            >
              Reset
            </Button>
            <Button
              variant="solid"
              color="primary"
              loading={isUploading}
              onClick={handleUpload}
              disabled={!formState.emails || isUploading || isEmailsUploading}
              className="rounded-full w-40"
              data-testid="upload-button"
            >
              Upload
            </Button>
          </div>
        </div>
      </div>
      {errorState && (
        <div className="animate-fade-in" data-testid="error-state">
          <ErrorState
            size="sm"
            title={errorState.title}
            description={errorState.description}
          />
        </div>
      )}
    </div>
  );
};

export default EmailUploader;
